import type { ReactNode } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import clsx from 'clsx';
import { Icon } from '../components/Icon';
import type { DragKind, DropTargetData } from './translate';

/** Attributes + listeners spread onto a DragHandle to start drags from it. */
export type SortableHandleProps = Record<string, unknown>;

export function SortableList({
  ids,
  children,
}: {
  readonly ids: readonly string[];
  readonly children: ReactNode;
}) {
  return (
    <SortableContext items={[...ids]} strategy={verticalListSortingStrategy}>
      {children}
    </SortableContext>
  );
}

/**
 * A sortable block-level node (sections, subsections). Hands its drag
 * handle props back so the caller renders a DragHandle wherever fits — never
 * inside an existing interactive element. Table rows can't nest in a div, so
 * TestCaseTable wires `useSortable` onto its own `<tr>`s instead.
 */
export function SortableNode({
  id,
  kind,
  label,
  className,
  children,
}: {
  readonly id: string;
  readonly kind: DragKind;
  readonly label: string;
  readonly className?: string;
  readonly children: (args: {
    handleProps: SortableHandleProps;
    listeners: SortableHandleProps;
    isDragging: boolean;
  }) => ReactNode;
}) {
  const { setNodeRef, transform, transition, isDragging, listeners, attributes } = useSortable({
    id,
    data: { kind, label },
  });
  // Pointer-only activator for the visible row/block. We deliberately DROP the
  // keyboard listener (onKeyDown) here: the block wraps inputs (inline node
  // creators), and the sortable's Space/Enter activation would otherwise
  // preventDefault those keys and eat spaces while typing. Keyboard reordering
  // stays on the focusable sr-only handle via `handleProps`.
  const pointerListeners: SortableHandleProps = (listeners as { onPointerDown?: unknown })
    ?.onPointerDown
    ? { onPointerDown: (listeners as { onPointerDown?: unknown }).onPointerDown }
    : {};
  return (
    <div
      ref={setNodeRef}
      className={className}
      data-dragging={isDragging ? 'true' : 'false'}
      style={{
        transform: CSS.Transform.toString(transform),
        ...(transition ? { transition } : {}),
      }}
    >
      {/* `handleProps` (attrs + listeners) goes on the accessible handle for
          keyboard reordering; `listeners` (pointer-only) is spread on the visible
          row so the whole element starts a pointer drag (no separate grip icon). */}
      {children({
        handleProps: { ...attributes, ...listeners },
        listeners: pointerListeners,
        isDragging,
      })}
    </div>
  );
}

/**
 * The drag handle. Spread `handleProps` from a sortable onto it. By default a
 * visible grip icon; with `srOnly` it renders no icon and is visually hidden
 * (kept in the DOM for keyboard reordering + as a stable a11y/test locator) —
 * used where the whole row is the pointer-drag target instead of a grip column.
 */
export function DragHandle({
  label,
  handleProps,
  className,
  srOnly = false,
}: {
  readonly label: string;
  readonly handleProps: SortableHandleProps;
  readonly className?: string;
  readonly srOnly?: boolean;
}) {
  if (srOnly) {
    return (
      <button
        type="button"
        aria-label={`Reorder ${label}`}
        onClick={(event) => event.stopPropagation()}
        className={clsx('sr-only', className)}
        {...handleProps}
      />
    );
  }
  return (
    <button
      type="button"
      aria-label={`Reorder ${label}`}
      onClick={(event) => event.stopPropagation()}
      className={clsx(
        'inline-flex w-4 cursor-grab touch-none items-center justify-center border-0 bg-transparent p-0',
        'text-text-muted hover:text-text-primary active:cursor-grabbing',
        className,
      )}
      {...handleProps}
    >
      <Icon name="gripVertical" size={14} />
    </button>
  );
}

/**
 * An append drop target for test-case drags: wrap a subsection row (works
 * even collapsed — the drag-out-of-Unsorted flow) or use as a table frame.
 * Dropping here appends the dragged case to the END of `subsectionId`.
 */
export function CaseDropTarget({
  subsectionId,
  kind,
  className,
  children,
}: {
  readonly subsectionId: string;
  readonly kind: DropTargetData['kind'];
  readonly className?: string;
  readonly children?: ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `${kind}:${subsectionId}`,
    data: { kind, subsectionId },
  });
  return (
    <div
      ref={setNodeRef}
      data-drop-over={isOver ? 'true' : 'false'}
      className={clsx('data-[drop-over=true]:bg-ink-50', className)}
    >
      {children}
    </div>
  );
}
